import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Button, useToast } from '../../components/primitives';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocBlockPair,
  DocRow,
  DocSignatures,
  DocInlineDate,
  DocInlineAresName,
  DocInlineAresLookup,
  DocActionButton,
  DocContactQR,
  DocDphSelect,
  DraftBanner,
  useDocChain,
  useDocTransferIn,
  DocChainBar,
  DocTransferButton,
  SavedDocsButton,
  NewDocButton,
  useDocRows,
  DocCustomRows,
  DocAddRowButton,
} from '../../components/document';
import { MapPickerButton } from '../../components/map';
import {
  CompanyPickerButton,
  SaveCompanyButton,
  ContactPickerButton,
  type AresPickResult,
  type CompanyPickResult,
  type ContactPerson,
  type ContactPickerButtonProps,
} from '../../components/ares';
import {
  StavbaPickerButton,
  SaveStavbaButton,
  type StavbaPickResult,
} from '../../components/stavba';
import { useSaveDocument } from '../../api/hooks';
import { useDocEditor } from '../../lib/useDocEditor';
import { useDocDraft } from '../../lib/useDocDraft';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import { useActiveStavba } from '../stavba-context/StavbaContext';

/**
 * Objednávka — "živý dokument".
 * Sekce: Údaje objednávky / Stavba / Objednatel / Dodavatel / Položky / Podpisy.
 */

interface PolozkaItem {
  id: string;
  popis: string;
  mnozstvi: string;
  jednotka: string;
  cena: string;
}

interface KontaktItem {
  id: string;
  jmeno: string;
  funkce: string;
  tel: string;
  email: string;
}

/** Další podpisové pole přidané z kontaktních osob (sekce 11/12). */
interface SignSlot {
  id: string;
  label: string;
  name: string;
  role: string;
  image: string;
}

const DEFAULT_INTRO =
  'Na základě cenové nabídky ze dne (č…….) u Vás tímto v rámci výše uvedeného díla objednáváme ……… (dále jen „dílo").';

const DEFAULT_PLATEBNI =
  'Fakturace bude zhotoviteli/dodavateli hrazena na základě vystavené faktury ze strany zhotovitele. Splatnost daňového dokladu (faktury) činí 35 dní ode dne doručení objednateli.\n\n' +
  'Fakturace bude zhotoviteli hrazena na základě vystavené faktury ze strany zhotovitele na základě skutečně odvedené práce a výkonu a bude zhotoviteli hrazena dílčími měsíčními platbami na konci měsíce. Splatnost daňového dokladu (faktury) činí 35 dní ode dne doručení objednateli.\n\n' +
  'Součástí konečného daňového dokladu (faktury) bude:\n' +
  '  — soupis provedených prací\n' +
  '  — kopie této objednávky\n' +
  '  — předávací protokol podepsaný objednatelem i zhotovitelem\n' +
  '  — zjišťovací protokol (v případě dílčí měsíční fakturace)\n\n' +
  'V případě potřeby — možné stažení VZORŮ výše uvedených DOKUMENTŮ: www.kvs-stavebni.cz → KE STAŽENÍ → Přihlásit → e-mail: dodavatele@kvs-stavebni.cz; heslo: dodavatele2025* → pod sekcí DODAVATELÉ možno stáhnout příslušný VZOR.';

const DEFAULT_POVINNOSTI =
  'Zhotovitel potvrzením této objednávky potvrzuje, že bude dodržovat zásady bezpečnosti a ochrany zdraví pracovníků (BOZP) a požární ochrany (PO) na stavbě, technologický postup prací a výstavby, který je zhotovitel objednateli povinen před zahájením prací předat a nechat si objednatelem odsouhlasit. Zhotovitel se dále zavazuje v rámci provádění díla dodržovat všechny platné právní předpisy vyplývající pro něj z povinností zaměstnavatele, zejména neumožnit výkon nelegální práce ve smyslu § 5 písm. e) zákona č. 435/2004 Sb., o zaměstnanosti.\n\n' +
  'Součástí závazku je dále povinnost zhotovitele zkompletovat a předat objednateli dokladovou část díla:\n' +
  '  — Doklady prokazující splnění technických požadavků na použité materiály a výrobky dle platných právních předpisů, vše v elektronické podobě a 1–3 tištěných provedeních;\n' +
  '  — Kompletní výchozí revize a zkoušky – zápisy o vyzkoušení technolog. zařízení, o provedených revizích a provozních zkouškách, v elektr. podobě a 1–3 tištěných provedeních;\n' +
  '  — Kopie platných záručních listů a podrobný seznam s označením konkrétních jednotlivých typů zařízení/výrobků s uvedením přesného termínu záruční doby od–do;\n' +
  '  — Provozní manuály, atesty, certifikáty, bezpečnostní listy, vše v elektron. podobě a 1–3 tištěných provedeních;\n' +
  '  — Doklady o uložení suti, zeminy a odpadů, kopie vážních a skládkových listů;\n' +
  '  — Návrhy uživatelských manuálů – podle potřeby.\n\n' +
  'V případě, že faktura nebude obsahovat požadované náležitosti, je objednatel oprávněn ji vrátit zpět k opravě. Lhůta splatnosti v takovém případě začne běžet znovu od doručení řádně opraveného dokladu.';

const DEFAULT_ZARUKA =
  '* Zhotovitel poskytuje objednateli záruku na provedení díla v délce 62 měsíců, není-li dále stanoveno jinak, která začíná plynout ode dne předání a převzetí bezvadného díla.\n' +
  '* V případě materiálů a výrobků spotřební povahy zhotovitel poskytuje objednateli záruku za jakost v souladu se záručními podmínkami výrobců těchto materiálů a výrobků, nejméně však v délce 24 měsíců, která začíná plynout ode dne předání a převzetí díla.\n' +
  '* Dílo má vady, pokud jeho provedení neodpovídá požadavkům uvedeným v této objednávce, příslušným ČSN, TKP, nebo jiné dokumentaci, vztahující se k provedení díla.\n' +
  '* Za vady díla, které se projevily po záruční době, odpovídá zhotovitel v případě, že jejich příčinou bylo porušení povinností zhotovitele.\n' +
  '* Objednatel je povinen reklamovat u zhotovitele vady písemně do 14 pracovních dnů ode dne, kdy vadu zjistil.\n' +
  '* Zhotovitel je povinen započít s odstraňováním reklamované vady do 5 dnů ode dne doručení písemného oznámení o vadě. Zhotovitel vadu odstraní v dohodnutém termínu, nejpozději však do 10 pracovních dní.\n' +
  '* Jestliže zhotovitel vadu neodstraní v dohodnutém termínu, je objednatel oprávněn na náklady zhotovitele vadu odstranit sám, nebo za pomocí třetí osoby.';

const DEFAULT_POKUTY =
  '* Pokud zhotovitel nedodrží termín dodání dle této objednávky, je povinen zaplatit objednateli smluvní pokuty ve výši 0,5 % z celkové ceny díla včetně DPH za každý i započatý den prodlení.\n' +
  '* Bude-li zhotovitel v prodlení s vyklizením staveniště v dohodnutém termínu, je objednatel oprávněn požadovat na zhotoviteli zaplacení smluvní pokuty ve výši 5 000,- Kč za každý započatý den prodlení s vyklizením staveniště.\n' +
  '* Nebude-li zhotovitel na místě staveniště a v jeho okolí udržovat pořádek a čistotu, odstraňovat veškerá znečištění a poškození komunikací, ke kterým dojde provozem zhotovitele, je povinen zaplatit objednateli smluvní pokutu ve výši 5 000,- Kč za každý jednotlivý případ porušení.\n' +
  '* Nebude-li zhotovitel dodržovat po dobu realizace díla BOZP a PO v prostorách staveniště, je povinen zaplatit objednateli smluvní pokutu ve výši 5 000,- Kč za každý jednotlivý případ porušení.\n' +
  '* Bude-li zhotovitel v prodlení se započetím nebo odstraněním reklamovaných vad, je povinen zaplatit objednateli smluvní pokutu ve výši 5 000,- Kč za každou vadu a každý započatý den prodlení.\n' +
  '* Nebude-li zhotovitel předem informovat objednatele o účasti subdodavatele na plnění předmětu dle této smlouvy, má objednatel právo požadovat zaplacení smluvní pokuty ve výši 5 000,- Kč za každý jednotlivý případ porušení.\n' +
  '* Uplatněním smluvní pokuty není dotčeno právo žádné strany žádat náhradu škody, a to i ve výši přesahující smluvní pokutu.';

function deriveInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => w && /[\p{L}]/u.test(w[0]) && !/^(s\.?r\.?o\.?|a\.?s\.?|spol|ltd|inc)$/i.test(w));
  if (words.length === 0) return '—';
  return words
    .slice(0, 3)
    .map((w) => w[0].toLocaleUpperCase('cs'))
    .join('');
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function parseCz(s: string): number {
  if (!s) return 0;
  const cleaned = s
    .replace(/\s/g, '')
    .replace(/Kč/gi, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '');
  const n = parseFloat(cleaned);
  return isFinite(n) ? n : 0;
}

function fmtCz(n: number): string {
  return (
    n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Kč'
  );
}

/**
 * Buňka tabulky kontaktů (sekce 11/12): jednořádková textarea, která roste
 * s obsahem — dlouhý e-mail nebo jméno se zalomí místo oříznutí v `<input>`.
 * Enter se ignoruje, nové řádky se do hodnoty nedostanou.
 */
function KontaktCellInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/[\r\n]+/g, ' '))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.preventDefault();
      }}
      style={cellTextarea}
      placeholder={placeholder}
    />
  );
}

/**
 * QR vizitka jednoho kontaktu — malý kód přímo v tabulce (jde i do PDF),
 * klik otevře velký QR + stažení vCard. Prázdný řádek nic nekreslí.
 */
function KontaktQRCell({ row, org }: { row: KontaktItem; org: string }) {
  return (
    <td style={{ ...tdStyle, padding: 3, textAlign: 'center', verticalAlign: 'middle', lineHeight: 0 }}>
      <DocContactQR
        preview
        name={row.jmeno}
        role={row.funkce}
        org={org}
        phone={row.tel}
        email={row.email}
        title={`QR vizitka — ${row.jmeno.trim()}`}
      />
    </td>
  );
}

/**
 * Akce u jednoho řádku kontaktní osoby (sekce 11/12): načíst z adresáře /
 * uložit do adresáře, označit jako podepisující osobu a odebrat řádek.
 */
function KontaktRowActions({
  row,
  attachToCompany,
  companyDraft,
  isSigner,
  signTitle,
  onPick,
  onToggleSign,
  onRemove,
}: {
  row: KontaktItem;
  attachToCompany: string;
  companyDraft?: ContactPickerButtonProps['companyDraft'];
  isSigner: boolean;
  signTitle: string;
  onPick: (p: ContactPerson) => void;
  onToggleSign: () => void;
  onRemove: () => void;
}) {
  return (
    <td style={{ ...tdStyle, padding: 4, whiteSpace: 'nowrap' }} className="docNoPrint">
      <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        <ContactPickerButton
          compact
          attachToCompany={attachToCompany}
          companyDraft={companyDraft}
          draftNew={{ jmeno: row.jmeno, funkce: row.funkce, tel: row.tel, email: row.email }}
          title="Načíst z adresáře / uložit do adresáře"
          onPick={onPick}
        />
        <button
          type="button"
          onClick={onToggleSign}
          title={signTitle}
          aria-pressed={isSigner}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 30,
            height: 30,
            borderRadius: 6,
            cursor: 'pointer',
            border: isSigner
              ? '1px solid var(--sd-yellow-border)'
              : '1px solid var(--sd-border)',
            background: isSigner ? 'var(--sd-yellow-soft)' : 'var(--sd-surface-2)',
            color: isSigner ? 'var(--sd-yellow-text)' : 'var(--sd-text-dim)',
            fontSize: 14,
            lineHeight: 1,
          }}
        >
          ✍
        </button>
        <DocActionButton onClick={onRemove} title="Odebrat kontakt">
          ✕
        </DocActionButton>
      </div>
    </td>
  );
}

export function ObjednavkaPage() {
  const saveDoc = useSaveDocument();
  const toast = useToast();
  const { activeStavba } = useActiveStavba();
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const [cislo, setCislo] = useState('');
  const [datumVystaveni, setDatumVystaveni] = useState('');
  const [terminZahajeni, setTerminZahajeni] = useState('');
  const [terminDokonceni, setTerminDokonceni] = useState('');
  const [mistoPlneni, setMistoPlneni] = useState('');
  const [dilo, setDilo] = useState('');
  const [intro, setIntro] = useState(DEFAULT_INTRO);
  const [textPlatebni, setTextPlatebni] = useState(DEFAULT_PLATEBNI);
  const [fakturacniAdresa, setFakturacniAdresa] = useState('');
  const [fakturacniEmail, setFakturacniEmail] = useState('');
  const [textPovinnosti, setTextPovinnosti] = useState(DEFAULT_POVINNOSTI);
  const [textZaruka, setTextZaruka] = useState(DEFAULT_ZARUKA);
  const [textPokuty, setTextPokuty] = useState(DEFAULT_POKUTY);
  const [prilohy, setPrilohy] = useState('');

  const [stavba, setStavba] = useState({
    nazev: '',
    adresa: '',
    cPovoleni: '',
  });

  const [obj, setObj] = useState({
    nazev: '',
    sidlo: '',
    ico: '',
    dic: '',
    banka: '',
    ucet: '',
  });

  const [dod, setDod] = useState({
    nazev: '',
    sidlo: '',
    ico: '',
    dic: '',
    banka: '',
    ucet: '',
  });

  const [kontaktyObj, setKontaktyObj] = useState<KontaktItem[]>([
    { id: newId('ko'), jmeno: '', funkce: '', tel: '', email: '' },
  ]);
  const [kontaktyDod, setKontaktyDod] = useState<KontaktItem[]>([
    { id: newId('kd'), jmeno: '', funkce: '', tel: '', email: '' },
  ]);

  // Označení osoby, která podepisuje za danou stranu (id řádku v sekci 11/12).
  const [signObjId, setSignObjId] = useState<string | null>(null);
  const [signDodId, setSignDodId] = useState<string | null>(null);
  const signObj = kontaktyObj.find((k) => k.id === signObjId) ?? null;
  const signDod = kontaktyDod.find((k) => k.id === signDodId) ?? null;

  /** Vloží kontakt z firmy do prvního prázdného řádku, jinak přidá nový. */
  const seedKontakt = (
    setList: React.Dispatch<React.SetStateAction<KontaktItem[]>>,
    prefix: string,
    person: { jmeno: string; tel: string; email: string },
  ) => {
    if (!person.jmeno.trim()) return;
    setList((list) => {
      const first = list[0];
      if (first && !first.jmeno.trim() && !first.tel.trim() && !first.email.trim()) {
        return list.map((x, i) =>
          i === 0 ? { ...x, jmeno: person.jmeno, tel: person.tel, email: person.email } : x,
        );
      }
      return [
        ...list,
        { id: newId(prefix), jmeno: person.jmeno, funkce: '', tel: person.tel, email: person.email },
      ];
    });
  };

  const [polozky, setPolozky] = useState<PolozkaItem[]>([
    { id: newId('p'), popis: '', mnozstvi: '', jednotka: '', cena: '' },
  ]);
  const [dphPct, setDphPct] = useState('21');

  // Podpisy: obrázky pro základní pole (objednatel/dodavatel se odvozují z
  // kontaktních osob u objednatele/dodavatele) + volitelná další podpisová pole
  // přidaná z kontaktních osob (sekce 11/12).
  const [sigImages, setSigImages] = useState({ objednatel: '', dodavatel: '' });
  const [extraSigs, setExtraSigs] = useState<SignSlot[]>([]);

  const addSigFromPerson = (origin: 'Objednatel' | 'Dodavatel', person: KontaktItem) =>
    setExtraSigs((list) => [
      ...list,
      {
        id: newId('sig'),
        label: origin,
        name: person.jmeno,
        role: person.funkce,
        image: '',
      },
    ]);
  const setSigImage = (id: string, image: string) =>
    setExtraSigs((list) => list.map((s) => (s.id === id ? { ...s, image } : s)));
  const removeSig = (id: string) =>
    setExtraSigs((list) => list.filter((s) => s.id !== id));

  // Skryté standardní řádky + vlastní řádky v hlavičkových sekcích.
  const chain = useDocChain();
  const rows = useDocRows();
  const { isHidden, hide, restoreSection, hiddenCount } = rows;

  const setStavbaPatch = (patch: Partial<typeof stavba>) =>
    setStavba((s) => ({ ...s, ...patch }));
  const setObjPatch = (patch: Partial<typeof obj>) => setObj((o) => ({ ...o, ...patch }));
  const setDodPatch = (patch: Partial<typeof dod>) => setDod((d) => ({ ...d, ...patch }));

  const addPolozka = () =>
    setPolozky((list) => [
      ...list,
      { id: newId('p'), popis: '', mnozstvi: '', jednotka: '', cena: '' },
    ]);
  const updatePolozka = (id: string, patch: Partial<PolozkaItem>) =>
    setPolozky((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const removePolozka = (id: string) =>
    setPolozky((list) => list.filter((x) => x.id !== id));

  const addKontaktObj = () =>
    setKontaktyObj((list) => [
      ...list,
      { id: newId('ko'), jmeno: '', funkce: '', tel: '', email: '' },
    ]);
  const updateKontaktObj = (id: string, patch: Partial<KontaktItem>) =>
    setKontaktyObj((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const removeKontaktObj = (id: string) => {
    setKontaktyObj((list) => list.filter((x) => x.id !== id));
    setSignObjId((cur) => (cur === id ? null : cur));
  };

  const addKontaktDod = () =>
    setKontaktyDod((list) => [
      ...list,
      { id: newId('kd'), jmeno: '', funkce: '', tel: '', email: '' },
    ]);
  const updateKontaktDod = (id: string, patch: Partial<KontaktItem>) =>
    setKontaktyDod((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const removeKontaktDod = (id: string) => {
    setKontaktyDod((list) => list.filter((x) => x.id !== id));
    setSignDodId((cur) => (cur === id ? null : cur));
  };

  const docTitle = cislo ? `Objednávka ${cislo}` : 'Objednávka';
  const metaLine = [
    datumVystaveni && `Vystaveno ${datumVystaveni}`,
    terminDokonceni && `Termín dokončení ${terminDokonceni}`,
  ]
    .filter(Boolean)
    .join(' · ');

  const { celkemBezDph, dphCastka, celkemSDph } = useMemo(() => {
    const bez = polozky.reduce((a, p) => a + parseCz(p.mnozstvi) * parseCz(p.cena), 0);
    const rate = (parseFloat(dphPct) || 0) / 100;
    const dph = bez * rate;
    return { celkemBezDph: bez, dphCastka: dph, celkemSDph: bez + dph };
  }, [polozky, dphPct]);

  /**
   * Předvyplní formulář z uloženého snímku dat. Používá se pro dokument
   * načtený z archivu (`?id=`) i pro obnovení lokálně rozpracovaného konceptu.
   */
  function applySnapshot(d: Record<string, unknown>) {
    if (d.cislo != null) setCislo(String(d.cislo));
    if (d.datumVystaveni != null) setDatumVystaveni(String(d.datumVystaveni));
    if (d.terminZahajeni != null) setTerminZahajeni(String(d.terminZahajeni));
    if (d.terminDokonceni != null) setTerminDokonceni(String(d.terminDokonceni));
    if (d.mistoPlneni != null) setMistoPlneni(String(d.mistoPlneni));
    if (d.dilo != null) setDilo(String(d.dilo));
    if (d.intro != null) setIntro(String(d.intro));
    if (d.textPlatebni != null) setTextPlatebni(String(d.textPlatebni));
    if (d.fakturacniAdresa != null) setFakturacniAdresa(String(d.fakturacniAdresa));
    if (d.fakturacniEmail != null) setFakturacniEmail(String(d.fakturacniEmail));
    if (d.textPovinnosti != null) setTextPovinnosti(String(d.textPovinnosti));
    if (d.textZaruka != null) setTextZaruka(String(d.textZaruka));
    if (d.textPokuty != null) setTextPokuty(String(d.textPokuty));
    if (d.prilohy != null) setPrilohy(String(d.prilohy));
    if (d.stavba) setStavba((s) => ({ ...s, ...(d.stavba as Partial<typeof stavba>) }));
    if (d.obj) setObj((x) => ({ ...x, ...(d.obj as Partial<typeof obj>) }));
    if (d.dod) setDod((x) => ({ ...x, ...(d.dod as Partial<typeof dod>) }));
    if (Array.isArray(d.polozky)) setPolozky(d.polozky as PolozkaItem[]);
    if (d.dphPct != null) setDphPct(String(d.dphPct));
    if (Array.isArray(d.kontaktyObj)) setKontaktyObj(d.kontaktyObj as KontaktItem[]);
    if (Array.isArray(d.kontaktyDod)) setKontaktyDod(d.kontaktyDod as KontaktItem[]);
    if (d.signObjId !== undefined) setSignObjId((d.signObjId as string | null) ?? null);
    if (d.signDodId !== undefined) setSignDodId((d.signDodId as string | null) ?? null);
    if (d.sigImages) setSigImages((p) => ({ ...p, ...(d.sigImages as Partial<typeof sigImages>) }));
    if (Array.isArray(d.extraSigs)) setExtraSigs(d.extraSigs as SignSlot[]);
    chain.apply(d);
    rows.apply(d.radky);
  }

  const { editId } = useDocEditor(applySnapshot);
  useDocTransferIn('objednavka', applySnapshot, editId);

  // Autosave rozpracovaného konceptu do prohlížeče (jen u nové objednávky).
  // Bez něj se při zavření/reloadu záložky ztratila celá rozepsaná objednávka.
  const draft = useDocDraft<Record<string, unknown>>('objednavka', editId == null);
  function restoreDraft() {
    if (!draft.pending) return;
    applySnapshot(draft.pending.data);
    draft.accept();
  }

  // Předvyplnění z Košilky (tlačítko „Předat do objednávky"). Aplikuje se jen pro
  // novou objednávku (bez ?id) a klíč se po načtení smaže, ať se nepoužije znovu.
  useEffect(() => {
    if (editId != null) return;
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem('kosilka_to_objednavka');
    } catch {
      return;
    }
    if (!raw) return;
    try {
      sessionStorage.removeItem('kosilka_to_objednavka');
      const d = JSON.parse(raw) as Record<string, unknown>;
      if (typeof d.cislo === 'string' && d.cislo) setCislo(d.cislo);
      if (typeof d.dilo === 'string' && d.dilo) setDilo(d.dilo);
      if (typeof d.terminZahajeni === 'string' && d.terminZahajeni) setTerminZahajeni(d.terminZahajeni);
      if (typeof d.terminDokonceni === 'string' && d.terminDokonceni) setTerminDokonceni(d.terminDokonceni);
      if (d.stavba) setStavba((s) => ({ ...s, ...(d.stavba as Partial<typeof stavba>) }));
      if (d.obj) setObj((o) => ({ ...o, ...(d.obj as Partial<typeof obj>) }));
      if (d.dod) setDod((x) => ({ ...x, ...(d.dod as Partial<typeof dod>) }));
      if (Array.isArray(d.polozky) && d.polozky.length) setPolozky(d.polozky as PolozkaItem[]);
    } catch {
      /* ignore poškozený prefill */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  /** Kompletní snímek dokumentu — ukládá se na server i do lokálního konceptu. */
  const docData = {
    cislo,
    datumVystaveni,
    terminZahajeni,
    terminDokonceni,
    mistoPlneni,
    dilo,
    intro,
    stavba,
    obj,
    dod,
    polozky,
    dphPct,
    textPlatebni,
    fakturacniAdresa,
    fakturacniEmail,
    textPovinnosti,
    textZaruka,
    textPokuty,
    kontaktyObj,
    kontaktyDod,
    signObjId,
    signDodId,
    prilohy,
    sigImages,
    extraSigs,
    radky: rows.snapshot(),
    zdroj: chain.zdroj,
  };

  useEffect(() => {
    draft.persist(docData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cislo, datumVystaveni, terminZahajeni, terminDokonceni, mistoPlneni, dilo, intro,
    stavba, obj, dod, polozky, dphPct, textPlatebni, fakturacniAdresa, fakturacniEmail,
    textPovinnosti, textZaruka, textPokuty, kontaktyObj, kontaktyDod, signObjId,
    signDodId, prilohy, sigImages, extraSigs, rows,
  , chain.zdroj]);

  /**
   * Uložení do archivu. Chybu je nutné ukázat — dřív se `mutateAsync` odmítlo
   * bez ošetření, takže uživatel viděl jen tlačítko „Uložit" a myslel si, že
   * je dokument uložený, přestože v archivu nikdy nepřistál.
   */
  async function handleSave(): Promise<number | null> {
    try {
      const res = await saveDoc.mutateAsync({
        id: editId ?? chain.savedId ?? undefined,
        doc_type: 'objednavka',
        title: docTitle,
        stavba: activeStavba?.nazev ?? '',
        data_json: docData,
      });
      draft.clear();
      toast.success('Objednávka uložena — najdete ji pod „Uložené" i v Archivu.');
      chain.markSaved(res.id);
      return res.id;
    } catch (e) {
      toast.error(docSaveErrorMessage(e));
      return null;
    }
  }

  return (
    <>
      <DocLayout
        breadcrumb={['Dokumenty', 'Objednávka']}
        title={docTitle}
        statusText="Rozpracovaný koncept"
        status="draft"
        actions={
          <>
            <NewDocButton draftKey="objednavka" path="/app/objednavka" />
            <SavedDocsButton
              docType="objednavka"
              title="Uložené objednávky"
              currentId={editId}
            />
            <Button
              variant="secondary"
              leading="⎙"
              onClick={() => printToPdf(`Objednavka_${cislo || 'koncept'}.pdf`)}
            >
              PDF
            </Button>
            <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
              Odeslat e-mailem
            </Button>
            <DocTransferButton
              docType="objednavka"
              getData={() => docData}
              save={handleSave}
              title={docTitle}
              zdroj={chain.zdroj}
            />
            <Button variant="primary" onClick={handleSave} disabled={saveDoc.isPending}>
              {saveDoc.isPending ? 'Ukládám…' : saveDoc.isSuccess ? 'Uloženo ✓' : 'Uložit'}
            </Button>
          </>
        }
      >
        <DocChainBar zdroj={chain.zdroj} />
        {draft.pending && (
          <DraftBanner
            savedAt={draft.pending.savedAt}
            onRestore={restoreDraft}
            onDiscard={draft.clear}
          />
        )}

        <div ref={previewRef}>
          <DocPage
            company={{
              initials: deriveInitials(obj.nazev),
              name: obj.nazev || '—',
              address: `${obj.sidlo || '—'}${obj.ico ? ' · IČ ' + obj.ico : ''}`,
            }}
            tag="Objednávka"
            title={
              <>
                {docTitle}{' '}
                <span style={{ fontWeight: 400, fontSize: 14, color: 'var(--sd-doc-dim)' }}>
                  · prací a dodávek
                </span>
              </>
            }
            meta={metaLine || undefined}
            footer={{ left: obj.nazev || '—', center: 'str. 1 / 1', right: cislo || '—' }}
          >
            <DocBlock
              idx="01"
              title="Údaje objednávky"
              hiddenCount={hiddenCount('s01')}
              onRestoreHidden={() => restoreSection('s01')}
              actions={<DocAddRowButton rows={rows} section="s01" />}
            >
              {!isHidden('s01.cislo') && (
                <DocRow
                  k="Číslo objednávky"
                  mono
                  value={cislo}
                  onChange={setCislo}
                  placeholder="např. OBJ-2026/18"
                  onRemove={() => hide('s01.cislo')}
                />
              )}
              {!isHidden('s01.vystaveno') && (
                <DocRow
                  k="Datum vystavení"
                  mono
                  value={datumVystaveni}
                  onChange={setDatumVystaveni}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate value={datumVystaveni} onChange={setDatumVystaveni} />
                  }
                  onRemove={() => hide('s01.vystaveno')}
                />
              )}
              {!isHidden('s01.zahajeni') && (
                <DocRow
                  k="Termín zahájení"
                  mono
                  value={terminZahajeni}
                  onChange={setTerminZahajeni}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate value={terminZahajeni} onChange={setTerminZahajeni} />
                  }
                  onRemove={() => hide('s01.zahajeni')}
                />
              )}
              {!isHidden('s01.dokonceni') && (
                <DocRow
                  k="Termín dokončení"
                  mono
                  value={terminDokonceni}
                  onChange={setTerminDokonceni}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate value={terminDokonceni} onChange={setTerminDokonceni} />
                  }
                  onRemove={() => hide('s01.dokonceni')}
                />
              )}
              {!isHidden('s01.misto') && (
                <DocRow
                  k="Místo plnění"
                  value={mistoPlneni}
                  onChange={setMistoPlneni}
                  placeholder="adresa staveniště"
                  actions={
                    <MapPickerButton
                      value={mistoPlneni}
                      onApply={setMistoPlneni}
                      title="Místo plnění"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s01.misto')}
                />
              )}
              <DocCustomRows rows={rows} section="s01" />
            </DocBlock>

            <DocBlock
              idx="02"
              title="Stavba"
              hiddenCount={hiddenCount('s02')}
              onRestoreHidden={() => restoreSection('s02')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s02" />
                  <StavbaPickerButton
                    title="Načíst stavbu ze seznamu"
                    onPick={(p: StavbaPickResult) =>
                      setStavba({
                        nazev: p.nazev,
                        adresa: p.adresa,
                        cPovoleni: p.stavebni_povoleni,
                      })
                    }
                  />
                  <SaveStavbaButton
                    title="Uložit stavbu"
                    build={() => ({
                      nazev: stavba.nazev,
                      adresa: stavba.adresa,
                      stavebni_povoleni: stavba.cPovoleni,
                      zakazka_cislo: '',
                      datum_zahajeni: '',
                    })}
                  />
                </>
              }
            >
              {!isHidden('s02.nazev') && (
                <DocRow
                  k="Název stavby"
                  value={stavba.nazev}
                  onChange={(v) => setStavbaPatch({ nazev: v })}
                  placeholder="např. Novostavba RD Březník"
                  onRemove={() => hide('s02.nazev')}
                />
              )}
              {!isHidden('s02.adresa') && (
                <DocRow
                  k="Adresa staveniště"
                  value={stavba.adresa}
                  onChange={(v) => setStavbaPatch({ adresa: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={stavba.adresa}
                      onApply={(v) => setStavbaPatch({ adresa: v })}
                      title="Adresa staveniště"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s02.adresa')}
                />
              )}
              {!isHidden('s02.povoleni') && (
                <DocRow
                  k="Číslo stavebního povolení"
                  value={stavba.cPovoleni}
                  onChange={(v) => setStavbaPatch({ cPovoleni: v })}
                  placeholder="např. MÚ-2026/0341"
                  onRemove={() => hide('s02.povoleni')}
                />
              )}
              <DocCustomRows rows={rows} section="s02" />
            </DocBlock>

            <DocBlockPair>
            <DocBlock
              idx="03"
              title="Objednatel"
              hiddenCount={hiddenCount('s03')}
              onRestoreHidden={() => restoreSection('s03')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s03" />
                  <CompanyPickerButton
                    preferRole="objednatel"
                    title="Načíst objednatele z adresáře"
                    onPick={(c: CompanyPickResult) => {
                      setObj((prev) => ({
                        ...prev,
                        nazev: c.nazev,
                        sidlo: c.adresa,
                        ico: c.ico,
                        dic: c.dic,
                      }));
                      seedKontakt(setKontaktyObj, 'ko', {
                        jmeno: c.kontakt_jmeno,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      });
                    }}
                  />
                  <SaveCompanyButton
                    title="Uložit objednatele do adresáře"
                    build={() => {
                      const kc = signObj ?? kontaktyObj.find((k) => k.jmeno.trim());
                      return {
                        role: 'objednatel',
                        nazev: obj.nazev,
                        ico: obj.ico,
                        dic: obj.dic,
                        adresa: obj.sidlo,
                        kontakt_jmeno: kc?.jmeno ?? '',
                        kontakt_tel: kc?.tel ?? '',
                        kontakt_email: kc?.email ?? '',
                      };
                    }}
                  />
                </>
              }
            >
              {!isHidden('s03.nazev') && (
                <DocRow
                  k="Název"
                  wrap
                  v={
                    <DocInlineAresName
                      value={obj.nazev}
                      placeholder="např. Stavby Novák s.r.o."
                      onChange={(v) => setObjPatch({ nazev: v })}
                      onPick={(d: AresPickResult) =>
                        setObjPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s03.nazev')}
                />
              )}
              {!isHidden('s03.sidlo') && (
                <DocRow
                  k="Sídlo"
                  wrap
                  value={obj.sidlo}
                  onChange={(v) => setObjPatch({ sidlo: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={obj.sidlo}
                      onApply={(v) => setObjPatch({ sidlo: v })}
                      title="Sídlo objednatele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s03.sidlo')}
                />
              )}
              {!isHidden('s03.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={obj.ico}
                  onChange={(v) => setObjPatch({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={obj.ico}
                      onResult={(d) =>
                        setObjPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s03.ico')}
                />
              )}
              {!isHidden('s03.dic') && (
                <DocRow
                  k="DIČ"
                  mono
                  value={obj.dic}
                  onChange={(v) => setObjPatch({ dic: v })}
                  placeholder="CZ12345678"
                  onRemove={() => hide('s03.dic')}
                />
              )}
              {!isHidden('s03.banka') && (
                <DocRow
                  k="Bankovní spojení"
                  wrap
                  value={obj.banka}
                  onChange={(v) => setObjPatch({ banka: v })}
                  placeholder="např. Komerční banka, a.s."
                  onRemove={() => hide('s03.banka')}
                />
              )}
              {!isHidden('s03.ucet') && (
                <DocRow
                  k="Číslo účtu"
                  mono
                  value={obj.ucet}
                  onChange={(v) => setObjPatch({ ucet: v })}
                  placeholder="123456789/0100"
                  onRemove={() => hide('s03.ucet')}
                />
              )}
              <DocCustomRows rows={rows} section="s03" />
            </DocBlock>

            <DocBlock
              idx="04"
              title="Dodavatel"
              hiddenCount={hiddenCount('s04')}
              onRestoreHidden={() => restoreSection('s04')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s04" />
                  <CompanyPickerButton
                    preferRole="subdodavatel"
                    title="Načíst dodavatele z adresáře"
                    onPick={(c: CompanyPickResult) => {
                      setDod((prev) => ({
                        ...prev,
                        nazev: c.nazev,
                        sidlo: c.adresa,
                        ico: c.ico,
                        dic: c.dic,
                      }));
                      seedKontakt(setKontaktyDod, 'kd', {
                        jmeno: c.kontakt_jmeno,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      });
                    }}
                  />
                  <SaveCompanyButton
                    title="Uložit dodavatele do adresáře"
                    build={() => {
                      const kc = signDod ?? kontaktyDod.find((k) => k.jmeno.trim());
                      return {
                        role: 'subdodavatel',
                        nazev: dod.nazev,
                        ico: dod.ico,
                        dic: dod.dic,
                        adresa: dod.sidlo,
                        kontakt_jmeno: kc?.jmeno ?? '',
                        kontakt_tel: kc?.tel ?? '',
                        kontakt_email: kc?.email ?? '',
                      };
                    }}
                  />
                </>
              }
            >
              {!isHidden('s04.nazev') && (
                <DocRow
                  k="Název"
                  wrap
                  v={
                    <DocInlineAresName
                      value={dod.nazev}
                      placeholder="např. Kraus Betonáže s.r.o."
                      onChange={(v) => setDodPatch({ nazev: v })}
                      onPick={(d: AresPickResult) =>
                        setDodPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s04.nazev')}
                />
              )}
              {!isHidden('s04.sidlo') && (
                <DocRow
                  k="Sídlo"
                  wrap
                  value={dod.sidlo}
                  onChange={(v) => setDodPatch({ sidlo: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={dod.sidlo}
                      onApply={(v) => setDodPatch({ sidlo: v })}
                      title="Sídlo dodavatele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s04.sidlo')}
                />
              )}
              {!isHidden('s04.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={dod.ico}
                  onChange={(v) => setDodPatch({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={dod.ico}
                      onResult={(d) =>
                        setDodPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s04.ico')}
                />
              )}
              {!isHidden('s04.dic') && (
                <DocRow
                  k="DIČ"
                  mono
                  value={dod.dic}
                  onChange={(v) => setDodPatch({ dic: v })}
                  placeholder="CZ12345678"
                  onRemove={() => hide('s04.dic')}
                />
              )}
              {!isHidden('s04.banka') && (
                <DocRow
                  k="Bankovní spojení"
                  wrap
                  value={dod.banka}
                  onChange={(v) => setDodPatch({ banka: v })}
                  placeholder="např. Komerční banka, a.s."
                  onRemove={() => hide('s04.banka')}
                />
              )}
              {!isHidden('s04.ucet') && (
                <DocRow
                  k="Číslo účtu"
                  mono
                  value={dod.ucet}
                  onChange={(v) => setDodPatch({ ucet: v })}
                  placeholder="123456789/0100"
                  onRemove={() => hide('s04.ucet')}
                />
              )}
              <DocCustomRows rows={rows} section="s04" />
            </DocBlock>
            </DocBlockPair>

            <DocBlock idx="05" title="Předmět objednávky">
              <DocRow
                k="Dílo"
                value={dilo}
                onChange={setDilo}
                placeholder="krátký název / předmět objednávky"
              />
              <DocRow
                k="Úvodní text"
                multiline
                rows={3}
                value={intro}
                onChange={setIntro}
                placeholder="Na základě cenové nabídky…"
              />
            </DocBlock>

            <DocBlock
              idx="06"
              title="Rozpis ceny díla"
              actions={
                <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                  <DocDphSelect value={dphPct} onChange={setDphPct} />
                  <DocActionButton onClick={addPolozka} title="Přidat položku">
                    + Položka
                  </DocActionButton>
                </span>
              }
            >
              <div style={{ margin: '4px 0 10px', overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Popis</th>
                      <th style={{ ...thStyle, width: 80 }}>Množství</th>
                      <th style={{ ...thStyle, width: 60 }}>MJ</th>
                      <th style={{ ...thStyle, width: 110 }}>J.cena bez DPH</th>
                      <th style={{ ...thStyle, width: 130 }}>Celkem bez DPH</th>
                      <th style={{ ...thStyle, width: 32 }} className="docNoPrint" />
                    </tr>
                  </thead>
                  <tbody>
                    {polozky.map((p) => {
                      const radek = parseCz(p.mnozstvi) * parseCz(p.cena);
                      return (
                        <Fragment key={p.id}>
                          <tr>
                            <td style={tdStyle}>
                              <input
                                value={p.popis}
                                onChange={(e) =>
                                  updatePolozka(p.id, { popis: e.target.value })
                                }
                                style={cellInputLeft}
                                placeholder="popis položky"
                              />
                            </td>
                            <td style={tdRight}>
                              <input
                                value={p.mnozstvi}
                                onChange={(e) =>
                                  updatePolozka(p.id, { mnozstvi: e.target.value })
                                }
                                style={cellInput}
                                placeholder="0"
                              />
                            </td>
                            <td style={tdRight}>
                              <input
                                value={p.jednotka}
                                onChange={(e) =>
                                  updatePolozka(p.id, { jednotka: e.target.value })
                                }
                                style={cellInput}
                                placeholder="ks"
                              />
                            </td>
                            <td style={tdRight}>
                              <input
                                value={p.cena}
                                onChange={(e) =>
                                  updatePolozka(p.id, { cena: e.target.value })
                                }
                                style={cellInput}
                                placeholder="0"
                              />
                            </td>
                            <td style={tdRight}>{fmtCz(radek)}</td>
                            <td style={{ ...tdStyle, padding: 4 }} className="docNoPrint">
                              <DocActionButton
                                onClick={() => removePolozka(p.id)}
                                title="Odebrat položku"
                              >
                                ✕
                              </DocActionButton>
                            </td>
                          </tr>
                        </Fragment>
                      );
                    })}
                    <tr>
                      <td style={{ ...tdStyle, fontWeight: 700 }} colSpan={4}>
                        Celkem bez DPH
                      </td>
                      <td style={{ ...tdRight, fontWeight: 700 }}>{fmtCz(celkemBezDph)}</td>
                      <td className="docNoPrint" />
                    </tr>
                    <tr>
                      <td style={tdStyle} colSpan={4}>
                        DPH {dphPct} %
                      </td>
                      <td style={tdRight}>{fmtCz(dphCastka)}</td>
                      <td className="docNoPrint" />
                    </tr>
                    <tr style={{ background: 'var(--sd-yellow-soft)' }}>
                      <td style={{ ...tdStyle, fontWeight: 800 }} colSpan={4}>
                        Celkem s DPH
                      </td>
                      <td style={{ ...tdRight, fontWeight: 800 }}>{fmtCz(celkemSDph)}</td>
                      <td className="docNoPrint" />
                    </tr>
                  </tbody>
                </table>
              </div>
            </DocBlock>

            <DocBlock idx="07" title="Platební podmínky">
              <DocRow
                k="Text"
                multiline
                rows={10}
                value={textPlatebni}
                onChange={setTextPlatebni}
              />
              <DocRow
                k="Fakturační adresa"
                value={fakturacniAdresa}
                onChange={setFakturacniAdresa}
                placeholder="doplňte fakturační adresu"
                actions={
                  <>
                    <CompanyPickerButton
                      compact
                      preferRole="objednatel"
                      title="Načíst fakturační adresu a e-mail z adresáře"
                      onPick={(c: CompanyPickResult) => {
                        setFakturacniAdresa(c.fakturacni_adresa || c.adresa);
                        const mail = c.fakturacni_email || c.kontakt_email;
                        if (mail) setFakturacniEmail(mail);
                      }}
                    />
                    <SaveCompanyButton
                      compact
                      title="Uložit fakturační adresu a e-mail k objednateli do adresáře"
                      build={() => ({
                        role: 'objednatel',
                        nazev: obj.nazev,
                        ico: obj.ico,
                        dic: obj.dic,
                        adresa: obj.sidlo,
                        fakturacni_adresa: fakturacniAdresa,
                        fakturacni_email: fakturacniEmail,
                      })}
                    />
                    <MapPickerButton
                      value={fakturacniAdresa}
                      onApply={setFakturacniAdresa}
                      title="Fakturační adresa"
                      label="Mapa"
                    />
                  </>
                }
              />
              <DocRow
                k="Fakturační e-mail"
                mono
                value={fakturacniEmail}
                onChange={setFakturacniEmail}
                placeholder="doplňte e-mail"
                inputType="email"
                actions={
                  <>
                    <CompanyPickerButton
                      compact
                      preferRole="objednatel"
                      title="Načíst fakturační adresu a e-mail z adresáře"
                      onPick={(c: CompanyPickResult) => {
                        setFakturacniAdresa(c.fakturacni_adresa || c.adresa);
                        const mail = c.fakturacni_email || c.kontakt_email;
                        if (mail) setFakturacniEmail(mail);
                      }}
                    />
                    <SaveCompanyButton
                      compact
                      title="Uložit fakturační adresu a e-mail k objednateli do adresáře"
                      build={() => ({
                        role: 'objednatel',
                        nazev: obj.nazev,
                        ico: obj.ico,
                        dic: obj.dic,
                        adresa: obj.sidlo,
                        fakturacni_adresa: fakturacniAdresa,
                        fakturacni_email: fakturacniEmail,
                      })}
                    />
                  </>
                }
              />
            </DocBlock>

            <DocBlock idx="08" title="Povinnosti zhotovitele">
              <DocRow
                k="Text"
                multiline
                rows={14}
                value={textPovinnosti}
                onChange={setTextPovinnosti}
              />
            </DocBlock>

            <DocBlock idx="09" title="Záruční podmínky">
              <DocRow
                k="Text"
                multiline
                rows={10}
                value={textZaruka}
                onChange={setTextZaruka}
              />
            </DocBlock>

            <DocBlock idx="10" title="Smluvní pokuty">
              <DocRow
                k="Text"
                multiline
                rows={10}
                value={textPokuty}
                onChange={setTextPokuty}
              />
            </DocBlock>

            <DocBlock
              idx="11"
              title="Kontaktní osoby objednatele"
              actions={
                <DocActionButton onClick={addKontaktObj} title="Přidat kontakt">
                  + Kontakt
                </DocActionButton>
              }
            >
              <div style={{ margin: '4px 0 10px', overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Jméno</th>
                      <th style={thStyle}>Funkce</th>
                      <th style={thStyle}>Telefon</th>
                      <th style={thStyle}>E-mail</th>
                      <th style={{ ...thStyle, width: 54 }} title="QR vizitka — naskenujte telefonem">
                        QR
                      </th>
                      <th style={{ ...thStyle, width: 116 }} className="docNoPrint">
                        Akce
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {kontaktyObj.map((k) => (
                      <tr key={k.id}>
                        <td style={tdStyle}>
                          <KontaktCellInput
                            value={k.jmeno}
                            onChange={(v) => updateKontaktObj(k.id, { jmeno: v })}
                            placeholder="Jméno"
                          />
                        </td>
                        <td style={tdStyle}>
                          <KontaktCellInput
                            value={k.funkce}
                            onChange={(v) => updateKontaktObj(k.id, { funkce: v })}
                            placeholder="funkce"
                          />
                        </td>
                        <td style={tdStyle}>
                          <KontaktCellInput
                            value={k.tel}
                            onChange={(v) => updateKontaktObj(k.id, { tel: v })}
                            placeholder="+420…"
                          />
                        </td>
                        <td style={tdStyle}>
                          <KontaktCellInput
                            value={k.email}
                            onChange={(v) => updateKontaktObj(k.id, { email: v })}
                            placeholder="email@…"
                          />
                        </td>
                        <KontaktQRCell row={k} org={obj.nazev} />
                        <KontaktRowActions
                          row={k}
                          attachToCompany={obj.nazev}
                          companyDraft={{ role: 'objednatel', ico: obj.ico, adresa: obj.sidlo }}
                          isSigner={signObjId === k.id}
                          signTitle="Označit jako osobu podepisující za objednatele"
                          onPick={(p) =>
                            updateKontaktObj(k.id, {
                              jmeno: p.jmeno,
                              funkce: p.funkce,
                              tel: p.tel,
                              email: p.email,
                            })
                          }
                          onToggleSign={() =>
                            setSignObjId((cur) => (cur === k.id ? null : k.id))
                          }
                          onRemove={() => removeKontaktObj(k.id)}
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DocBlock>

            <DocBlock
              idx="12"
              title="Kontaktní osoby dodavatele"
              actions={
                <DocActionButton onClick={addKontaktDod} title="Přidat kontakt">
                  + Kontakt
                </DocActionButton>
              }
            >
              <div style={{ margin: '4px 0 10px', overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Jméno</th>
                      <th style={thStyle}>Funkce</th>
                      <th style={thStyle}>Telefon</th>
                      <th style={thStyle}>E-mail</th>
                      <th style={{ ...thStyle, width: 54 }} title="QR vizitka — naskenujte telefonem">
                        QR
                      </th>
                      <th style={{ ...thStyle, width: 116 }} className="docNoPrint">
                        Akce
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {kontaktyDod.map((k) => (
                      <tr key={k.id}>
                        <td style={tdStyle}>
                          <KontaktCellInput
                            value={k.jmeno}
                            onChange={(v) => updateKontaktDod(k.id, { jmeno: v })}
                            placeholder="Jméno"
                          />
                        </td>
                        <td style={tdStyle}>
                          <KontaktCellInput
                            value={k.funkce}
                            onChange={(v) => updateKontaktDod(k.id, { funkce: v })}
                            placeholder="funkce"
                          />
                        </td>
                        <td style={tdStyle}>
                          <KontaktCellInput
                            value={k.tel}
                            onChange={(v) => updateKontaktDod(k.id, { tel: v })}
                            placeholder="+420…"
                          />
                        </td>
                        <td style={tdStyle}>
                          <KontaktCellInput
                            value={k.email}
                            onChange={(v) => updateKontaktDod(k.id, { email: v })}
                            placeholder="email@…"
                          />
                        </td>
                        <KontaktQRCell row={k} org={dod.nazev} />
                        <KontaktRowActions
                          row={k}
                          attachToCompany={dod.nazev}
                          companyDraft={{ role: 'zhotovitel', ico: dod.ico, adresa: dod.sidlo }}
                          isSigner={signDodId === k.id}
                          signTitle="Označit jako osobu podepisující za dodavatele"
                          onPick={(p) =>
                            updateKontaktDod(k.id, {
                              jmeno: p.jmeno,
                              funkce: p.funkce,
                              tel: p.tel,
                              email: p.email,
                            })
                          }
                          onToggleSign={() =>
                            setSignDodId((cur) => (cur === k.id ? null : k.id))
                          }
                          onRemove={() => removeKontaktDod(k.id)}
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DocBlock>

            <DocBlock idx="13" title="Přílohy">
              <DocRow
                k="Text"
                multiline
                rows={3}
                value={prilohy}
                onChange={setPrilohy}
                placeholder="seznam příloh"
              />
            </DocBlock>

            {/*
              Podpisy se odvozují z kontaktních osob u objednatele a dodavatele.
              Další podpisová pole lze přidat z osob v sekcích 11 a 12.
            */}
            <div
              className="docNoPrint"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                margin: '18px 0 4px',
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: 'var(--sd-doc-dim)',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                Podpisy
              </span>
              <select
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  const [origin, id] = v.split('|');
                  const src = origin === 'obj' ? kontaktyObj : kontaktyDod;
                  const person = src.find((k) => k.id === id);
                  if (person) {
                    addSigFromPerson(origin === 'obj' ? 'Objednatel' : 'Dodavatel', person);
                  }
                  e.target.value = '';
                }}
                aria-label="Přidat podpisové pole z kontaktních osob"
                style={{
                  padding: '5px 8px',
                  border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)',
                  borderRadius: 4,
                  background: '#fff',
                  color: 'var(--sd-doc-ink)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <option value="">+ Přidat podpisové pole z osob…</option>
                {kontaktyObj.filter((k) => k.jmeno.trim()).length > 0 && (
                  <optgroup label="Objednatel (sekce 11)">
                    {kontaktyObj
                      .filter((k) => k.jmeno.trim())
                      .map((k) => (
                        <option key={k.id} value={`obj|${k.id}`}>
                          {k.jmeno}
                          {k.funkce ? ` — ${k.funkce}` : ''}
                        </option>
                      ))}
                  </optgroup>
                )}
                {kontaktyDod.filter((k) => k.jmeno.trim()).length > 0 && (
                  <optgroup label="Dodavatel (sekce 12)">
                    {kontaktyDod
                      .filter((k) => k.jmeno.trim())
                      .map((k) => (
                        <option key={k.id} value={`dod|${k.id}`}>
                          {k.jmeno}
                          {k.funkce ? ` — ${k.funkce}` : ''}
                        </option>
                      ))}
                  </optgroup>
                )}
              </select>
            </div>

            <DocSignatures
              items={[
                {
                  label: 'Objednatel',
                  name: signObj?.jmeno || obj.nazev || '—',
                  role: signObj?.funkce || '',
                  image: sigImages.objednatel,
                  onSign: (v) => setSigImages((s) => ({ ...s, objednatel: v })),
                },
                {
                  label: 'Dodavatel',
                  name: signDod?.jmeno || dod.nazev || '—',
                  role: signDod?.funkce || '',
                  image: sigImages.dodavatel,
                  onSign: (v) => setSigImages((s) => ({ ...s, dodavatel: v })),
                },
                ...extraSigs.map((s) => ({
                  label: s.label,
                  name: s.name || '—',
                  role: s.role || '',
                  image: s.image,
                  onSign: (v: string) => setSigImage(s.id, v),
                  onRemove: () => removeSig(s.id),
                })),
              ]}
            />
          </DocPage>
        </div>
      </DocLayout>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        docType="objednavka"
        docTitle={docTitle}
        defaultTo={signDod?.email || kontaktyDod.find((k) => k.email.trim())?.email || ''}
        defaultSubject={docTitle}
        meta={`Dodavatel: ${dod.nazev || '—'}${
          activeStavba?.nazev ? ' · ' + activeStavba.nazev : ''
        }`}
      />
    </>
  );
}

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
  color: 'var(--sd-doc-ink)',
};
const thStyle: CSSProperties = {
  padding: '6px 8px',
  border: '1px solid #d0d5de',
  background: 'rgba(18,50,90,0.06)',
  fontWeight: 700,
  fontSize: 10.5,
  textAlign: 'center',
  color: 'var(--sd-doc-ink)',
  lineHeight: 1.3,
};
const tdStyle: CSSProperties = {
  padding: '6px 8px',
  border: '1px solid #d0d5de',
  fontSize: 11.5,
};
const tdRight: CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  fontFamily: 'var(--sd-font-mono)',
};
const cellInput: CSSProperties = {
  width: '100%',
  background: 'transparent',
  border: 0,
  borderBottom: '1px dashed #d0d5de',
  outline: 0,
  padding: '2px 0',
  textAlign: 'right',
  fontFamily: 'var(--sd-font-mono)',
  fontSize: 11.5,
  color: 'var(--sd-doc-ink)',
};
const cellInputLeft: CSSProperties = {
  ...cellInput,
  textAlign: 'left',
  fontFamily: 'inherit',
};
const cellTextarea: CSSProperties = {
  ...cellInputLeft,
  display: 'block',
  resize: 'none',
  overflow: 'hidden',
  lineHeight: 1.35,
  minWidth: 0,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};
